import styled, { css } from 'styled-components/macro';
import { ModalTemplateSettings } from './types';

export interface ScModalTemplateProps {
  settings: ModalTemplateSettings;
  height?: number | null;
}

export const ScModalTemplate = styled.div<ScModalTemplateProps>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: ${({ settings }) =>
    settings.type === 'mobile' && settings.mode.type === 'bottom'
      ? 'flex-end'
      : 'center'};
  min-height: ${({ height }) => (height ? `${height}px` : '100vh')};
  min-height: 100dvh;

  & > .container {
    width: 100%;
    position: relative;
    margin: 0 auto;
    color: ${({ theme }) => theme.secondaryColor};
    max-width: ${({ settings }) => {
      switch (settings.type) {
        case 'desktop':
          switch (settings.size) {
            case 'small':
              return '568px';
            case 'xSmall':
              return '528px';
            case 'xLarge':
              return '896px';
            case 'large':
            default:
              return '768px';
          }
        case 'mobile':
        default:
          return '100%';
      }
    }};

    padding: ${({ settings }) => {
      switch (settings.type) {
        case 'mobile':
          switch (settings.mode.type) {
            case 'background':
            case 'background-custom':
              return '0px';
            case 'bottom':
              return '64px 0px 0px';
            case 'insideCrossButton':
              return '16px 12px';
            case 'default':
            default:
              return '64px 12px 12px';
          }
        case 'desktop':
        default:
          return '12px 64px';
      }
    }};

    & > .title {
      font-weight: 700;
      font-size: 18px;
      line-height: 1.33;
      letter-spacing: 0.01em;
      color: ${({ theme }) => theme.whiteColor};
      padding: 8px 64px 8px 12px;
      margin-bottom: 12px;
      margin-top: -52px;
    }

    & > .inner {
      position: relative;

      ${({ settings }) => {
        switch (settings.type) {
          case 'mobile':
            switch (settings.mode.type) {
              case 'background':
                return css`
                  display: flex;
                  flex-direction: column;
                  align-items: center;
                  justify-content: center;
                  min-height: 100vh;
                  min-height: 100dvh;
                  background-image: url(${settings.mode.src});
                  background-size: cover;
                  background-repeat: no-repeat;
                  background-position: top center;
                  padding: 64px 12px 12px;
                  border-radius: 0;
                `;
              case 'background-custom':
                return css`
                  display: flex;
                  flex-direction: column;
                  align-items: center;
                  justify-content: center;
                  min-height: 100vh;
                  min-height: 100dvh;
                  background-image: url(${settings.mode.src});
                  background-size: cover;
                  background-repeat: no-repeat;
                  background-position: ${settings.mode.position ||
                  'top center'};
                  padding: 0;
                  border-radius: 0;
                `;
              case 'bottom':
              case 'default':
              default:
                return css`
                  border-radius: 16px;
                `;
            }
          case 'desktop':
          default:
            switch (settings.mode.type) {
              case 'background':
                return css`
                  padding: 24px;
                  border-radius: 16px;
                  background-image: url(${settings.mode.src});
                  background-size: cover;
                  background-repeat: no-repeat;
                  background-position: top center;
                `;
              case 'background-custom':
                return css`
                  border-radius: 16px;
                  background-image: url(${settings.mode.src});
                  background-size: cover;
                  background-repeat: no-repeat;
                  background-position: ${settings.mode.position ||
                  'top center'};
                `;
              default:
                return '';
            }
        }
      }};

      & > .header {
        margin-bottom: ${({ settings }) =>
          settings.type === 'mobile' ? '32px' : '24px'};
      }

      & > .body {
        width: 100%;

        ${({ settings }) => {
          switch (settings.type) {
            case 'mobile':
              switch (settings.mode.type) {
                case 'background':
                case 'insideCrossButton':
                  return css`
                    border-radius: 16px;
                    background: ${({ theme }) => theme.grayColor5};
                    padding: ${settings.smallYPadding
                      ? '8px 16px'
                      : settings.smallXPadding
                      ? '24px 12px'
                      : '24px 16px'};
                  `;
                case 'background-custom':
                  return css``;
                case 'bottom':
                  switch (settings.footerMode) {
                    case 'wide':
                      return css`
                        border-radius: 16px;
                        background: ${({ theme }) => theme.grayColor5};
                        padding: 24px 12px;
                      `;
                    case 'default':
                    default:
                      return css`
                        border-radius: 16px;
                        background: ${({ theme }) => theme.grayColor5};
                        padding: ${settings.smallYPadding
                          ? '8px 16px'
                          : settings.smallXPadding
                          ? '24px 12px'
                          : '24px 16px'};
                        border-top-left-radius: 16px;
                        border-top-right-radius: 16px;
                        border-bottom-left-radius: 0px;
                        border-bottom-right-radius: 0px;
                      `;
                  }

                case 'default':
                default:
                  return css`
                    background: ${({ theme }) => theme.grayColor5};
                    padding: ${settings.smallXPadding
                      ? `24px 12px`
                      : `24px 16px`};
                    border-radius: 16px;
                  `;
              }
            case 'desktop':
            default:
              switch (settings.mode.type) {
                case 'background-custom':
                  return css``;
                case 'background':
                default:
                  return css`
                    background: ${({ theme }) => theme.grayColor5};
                    padding: 32px 24px;
                    border-radius: 16px;
                  `;
              }
          }
        }}

        & > .headline {
          margin-bottom: 32px;
          text-align: center;
          letter-spacing: 0.01em;
          font-weight: 700;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: ${({ settings }) => (settings?.headlineGapSm ? '8px' : '12px')};

          & > .title {
            color: ${({ theme }) => theme.whiteColor};
            ${({ settings }) =>
              settings.type === 'mobile'
                ? css`
                    font-size: 18px;
                    line-height: 24px;
                  `
                : css`
                    font-size: 22px;
                    line-height: 28px;
                  `}
          }

          & > .description {
            ${({ settings }) =>
              settings.type === 'mobile'
                ? css`
                    font-size: 14px;
                    line-height: 20px;
                  `
                : css`
                    font-size: 16px;
                    line-height: 28px;
                  `}
          }
        }
      }

      & > .footer {
        background: ${({ theme }) => theme.grayColor5};
        margin-top: 12px;

        ${({ settings }) => {
          if (settings.type === 'mobile' && settings.footerMode === 'wide') {
            return css`
              border-radius: 16px 16px 0px 0px;
              padding: 24px 12px;
            `;
          }

          return css`
            border-radius: 16px;
            padding: 14px 24px;
            width: 100%;
          `;
        }}
      }
    }
  }
`;
